import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { useMe, usePaging } from "../hooks.ts";
import { Badge, Button, Card, Dialog, ErrorText, Input, PageHeader, Pagination, Spinner } from "../components/ui.tsx";
import { AudienceBadge } from "../components/knowledge.tsx";
import { fmtDate, MemoryExplorer, SharePill, type MemItem } from "../components/memory-map.tsx";

type Bank = { items: MemItem[]; total: number; truncated: boolean; ready: boolean; off: boolean };
const load = (scope: "mine" | "team" | "forgotten") => unwrap(api.me.memory.bank.$get({ query: { scope } })) as Promise<Bank>;

/** Memory → My memory, or Team & company (read only). */
export function MemoryTab({ scope }: { scope: "mine" | "team" }) {
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery({ queryKey: ["my-memory", scope], queryFn: () => load(scope) });
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  const [asking, setAsking] = useState<MemItem | null>(null);
  const impersonating = me.data?.user.impersonatedBy;

  const patchItem = (id: string, change: Partial<MemItem>) =>
    qc.setQueryData<Bank>(["my-memory", "mine"], (d) => d && { ...d, items: d.items.map((i) => (i.id === id ? { ...i, ...change } : i)) });
  const forget = useMutation({
    mutationFn: ({ item, unshare }: { item: MemItem; unshare: boolean }) => unwrap(api.me.memory.items[":id"].forget.$post({ param: { id: item.id }, json: { unshare } })),
    onSuccess: (r, { item }) => {
      setGone((g) => new Set(g).add(item.id));
      setAsking(null);
      if (r.unshared) {
        patchItem(item.id, { shared: null });
        void qc.invalidateQueries({ queryKey: ["my-promotions"] });
      }
      void qc.invalidateQueries({ queryKey: ["my-memory", "forgotten"] });
    },
  });
  const restore = useMutation({
    mutationFn: (item: MemItem) => unwrap(api.me.memory.items[":id"].restore.$post({ param: { id: item.id } })),
    onSuccess: (_r, item) => {
      setGone((g) => {
        const n = new Set(g);
        n.delete(item.id);
        return n;
      });
      void qc.invalidateQueries({ queryKey: ["my-memory", "forgotten"] });
    },
  });
  const correct = async (item: MemItem, text: string) => {
    await unwrap(api.me.memory.items[":id"].$patch({ param: { id: item.id }, json: { text } }));
    patchItem(item.id, { text });
  };

  const header =
    scope === "mine" ? (
      <PageHeader title="My memory" description="What your assistant remembers about your work, so you don't have to repeat yourself. Correct anything that's wrong, and forget anything it shouldn't keep." />
    ) : (
      <PageHeader
        title="Team & company"
        description={`What your assistant can also recall: knowledge shared within ${me.data?.group?.name ?? "your group"} and across the company, including synced documents. Read only; admins look after it.`}
      />
    );
  if (q.isPending) return <>{header}<div className="grid place-items-center py-16"><Spinner /></div></>;
  if (q.error) return <>{header}<ErrorText error={q.error} /></>;
  const bank = q.data;

  if (scope === "team" && bank.off)
    return <>{header}<Card className="p-6 text-sm text-[var(--muted)]">Company memory is switched off at the moment, so your assistant recalls only its own memory.</Card></>;
  if (!bank.items.length)
    return (
      <>
        {header}
        <Card className="px-6 py-10 text-center text-sm text-[var(--muted)]">
          {scope === "mine" ? (
            <>
              <div className="mb-1 text-base font-medium text-[var(--text)]">Nothing remembered yet</div>
              Chat with your assistant about your work. It remembers facts about clients, projects and how you like things done, and they show up here.
            </>
          ) : (
            "Nothing has been shared with your team or the company yet."
          )}
        </Card>
        {scope === "mine" && <Forgotten restore={restore.mutate} busy={restore.isPending} />}
      </>
    );

  const canChange = scope === "mine" && !impersonating;
  const recent = scope === "mine" ? bank.items.filter((i) => i.type !== "observation" && i.at && Date.now() - new Date(i.at).getTime() < 7 * 86_400_000).slice(0, 4) : [];
  const actions = canChange
    ? (i: MemItem) =>
        gone.has(i.id) ? (
          <button onClick={() => restore.mutate(i)} disabled={restore.isPending} className="text-[var(--color-accent)] hover:underline">
            Undo forget
          </button>
        ) : i.editable ? (
          <button onClick={() => (i.shared ? setAsking(i) : forget.mutate({ item: i, unshare: false }))} disabled={forget.isPending} className="text-red-700 hover:underline">
            Forget
          </button>
        ) : null
    : undefined;

  return (
    <>
      {header}
      {impersonating && scope === "mine" && (
        <Card className="mb-4 border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">You're acting as {me.data?.user.name}: you can look, but only they can correct or forget their memories.</Card>
      )}
      {bank.truncated && <p className="mb-3 text-xs text-[var(--muted)]">Showing the newest {bank.items.length.toLocaleString()} memories{scope === "mine" ? ` of ${bank.total.toLocaleString()}` : ""}.</p>}
      <ErrorText error={forget.error ?? restore.error} />
      <MemoryExplorer
        items={bank.items}
        scope={scope}
        gone={gone}
        onCorrect={canChange ? correct : undefined}
        actions={actions}
        intro={(go, edit) =>
          !!recent.length && (
            <Card className="mb-4 p-4">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                Learned this week <Badge>{recent.length} new</Badge>
              </h2>
              <ul className="space-y-2.5">
                {recent.map((i) => (
                  <li key={i.id} className="text-sm">
                    <div className={gone.has(i.id) ? "text-[var(--muted)] line-through" : ""}>{i.text}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--muted)]">
                      <span>{fmtDate(i.at)}</span>
                      {!!i.topics.length && (
                        <span>
                          · about{" "}
                          {i.topics.slice(0, 3).map((t, n) => (
                            <span key={t}>
                              {n > 0 && ", "}
                              <button onClick={() => go(t)} className="text-[var(--color-accent)] hover:underline">
                                {t}
                              </button>
                            </span>
                          ))}
                        </span>
                      )}
                      <SharePill item={i} scope="mine" />
                      <span className="ml-auto flex items-center gap-3">
                        {canChange && i.editable && !gone.has(i.id) && (
                          <button onClick={() => edit(i)} className="text-[var(--color-accent)] hover:underline">
                            Correct
                          </button>
                        )}
                        {actions?.(i)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )
        }
        listFooter={scope === "mine" && <Forgotten restore={restore.mutate} busy={restore.isPending} />}
      />
      <Dialog open={!!asking} onClose={() => setAsking(null)} title="Forget this memory?">
        <p className="text-sm">
          Your assistant stops using it. It was also shared with {asking?.shared?.audience === "company" ? "everyone in the company" : (asking?.shared?.groupName ?? "your group")}
          {asking?.shared?.text ? <> as “{asking.shared.text}”</> : null}.
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={() => setAsking(null)}>Cancel</Button>
          <Button disabled={forget.isPending} onClick={() => asking && forget.mutate({ item: asking, unshare: false })}>Forget, keep it shared</Button>
          <Button variant="primary" disabled={forget.isPending} onClick={() => asking && forget.mutate({ item: asking, unshare: true })}>Forget and unshare</Button>
        </div>
      </Dialog>
    </>
  );
}

/** Memories forgotten earlier, to bring back. Loaded only when opened. */
function Forgotten({ restore, busy }: { restore: (i: MemItem) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ["my-memory", "forgotten"], queryFn: () => load("forgotten"), enabled: open });
  const me = useMe();
  return (
    <div className="mt-6">
      <button onClick={() => setOpen(!open)} className="text-sm text-[var(--color-accent)] hover:underline" aria-expanded={open}>
        {open ? "Hide forgotten memories" : "Forgotten memories"}
      </button>
      {open && (
        <Card className="mt-2 divide-y divide-[var(--line)]">
          {q.isPending && <div className="grid place-items-center py-6"><Spinner /></div>}
          <ErrorText error={q.error} />
          {q.data && !q.data.items.length && <p className="px-4 py-4 text-sm text-[var(--muted)]">Nothing forgotten.</p>}
          {q.data?.items.map((i) => (
            <div key={i.id} className="flex flex-wrap items-start gap-3 px-4 py-3 text-sm">
              <div className="min-w-0 flex-1 basis-60">
                <div className="text-[var(--muted)]">{i.text}</div>
                <div className="mt-1 text-xs text-[var(--muted)]">Forgotten {fmtDate(i.forgottenAt ?? null)}</div>
              </div>
              {!me.data?.user.impersonatedBy && (
                <Button className="h-8" disabled={busy} onClick={() => restore(i)}>
                  Bring back
                </Button>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/** Memory → Shared by me: what went to the person's group or the company (the scrubbed wording), with Remove. */
export function SharedByMePage() {
  const qc = useQueryClient();
  const me = useMe();
  const paging = usePaging([], 25);
  const q = useQuery({
    queryKey: ["my-promotions", paging.page, paging.pageSize, paging.q],
    queryFn: () => unwrap(api.me.memory.promotions.$get({ query: { page: String(paging.page), pageSize: String(paging.pageSize), ...(paging.q && { q: paging.q }) } })),
    placeholderData: keepPreviousData,
  });
  useEffect(() => void paging.clamp(q.data?.total), [q.data?.total]); // eslint-disable-line react-hooks/exhaustive-deps
  const withdraw = useMutation({
    mutationFn: (id: number) => unwrap(api.me.memory.promotions[":id"].$delete({ param: { id: String(id) } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["my-promotions"] });
      void qc.invalidateQueries({ queryKey: ["my-memory"] });
    },
  });
  const level = q.data?.level;
  const groupName = q.data?.groupName;
  return (
    <>
      <PageHeader
        title="Shared by me"
        description={
          !q.data
            ? ""
            : !q.data.promotionEnabled
              ? "Sharing from people's assistants is switched off at the moment. Anything shared earlier is listed here; remove what you'd rather keep private."
              : level === "off"
                ? "Your group doesn't share memories at the moment. Anything shared earlier is listed here; remove what you'd rather keep private."
                : level === "group"
                  ? `Work knowledge your assistant learned can be shared, without personal details, with the rest of ${groupName ?? "your group"} — never company-wide. Remove anything you'd rather keep private.`
                  : `Work knowledge your assistant learned can be shared, without personal details, with ${groupName ? `the rest of ${groupName}` : "your group"} or with the whole company, so colleagues' assistants know it too. Remove anything you'd rather keep private.`
        }
      />
      {q.isPending && <div className="grid place-items-center py-16"><Spinner /></div>}
      <ErrorText error={q.error ?? withdraw.error} />
      {q.data && (
        <>
          {(paging.q || q.data.total > paging.pageSize) && (
            <Input value={paging.search} onChange={(e) => paging.setSearch(e.target.value)} placeholder="Search what you've shared…" className="mb-3 max-w-sm" />
          )}
          <Card className="divide-y divide-[var(--line)]">
            {q.data.promotions.length === 0 && <div className="px-5 py-6 text-sm text-[var(--muted)]">{paging.q ? "Nothing matches that search." : "Nothing shared yet."}</div>}
            {q.data.promotions.map((p) => (
              <div key={p.id} className="flex items-start gap-4 px-5 py-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div>{p.sharedText}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--muted)]">
                    <AudienceBadge audience={p.audience} groupName={p.groupName} own />
                    <span>{fmtDate(p.createdAt)}</span>
                  </div>
                </div>
                {!me.data?.user.impersonatedBy && (
                  <Button disabled={withdraw.isPending} onClick={() => withdraw.mutate(p.id)}>
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </Card>
          <Pagination page={paging.page} pageSize={paging.pageSize} total={q.data.total} onPage={paging.setPage} onPageSize={paging.setPageSize} busy={q.isFetching} />
        </>
      )}
    </>
  );
}
